import { StepFooter } from '@/shared/components'
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  SelectListMulti,
  RadioGroup,
  InputGroup,
  Alert,
  ElectricityProductionMethod
} from '@/common'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SelectedElectricityProductionMethodsPayload } from '@/features/production-routes/api/ElectricityProductionMethodsModel'

type State = Record<
  ElectricityProductionMethod,
  {
    isSelected: boolean
    isRenewable?: boolean
    info?: string
  }
>

function MethodForm({
  info,
  name,
  isRenewable,
  updateInfoState,
  udpateRenewableState,
  infoLabel
}: {
  isRenewable: boolean
  name: string
  info: string
  updateInfoState: (info: string) => void
  udpateRenewableState: (isRenewable: boolean) => void
  infoLabel: string
}) {
  const { t } = useTranslation()
  return (
    <div className="space-y-4">
      <RadioGroup
        label={t('productionRoute:electricity.methodForm.isRenewable.label')}
        name={name}
        value={isRenewable}
        onChange={isRenewable => udpateRenewableState(isRenewable)}
        options={[
          {
            label: t('common:yes'),
            value: true
          },
          {
            label: t('common:no'),
            value: false
          }
        ]}
      />
      <InputGroup
        label={infoLabel}
        value={info}
        onChange={event => updateInfoState(event.target.value)}
        name="details"
        className="border data-[hover]:shadow data-[focus]:bg-blue-100 w-full"
      />
    </div>
  )
}

const initState = (selectedMethods: SelectedElectricityProductionMethodsPayload): State => {
  const grid = selectedMethods.find(method => method.type === ElectricityProductionMethod.GRID)
  const directTechnicalLink = selectedMethods.find(
    method => method.type === ElectricityProductionMethod.DIRECT_TECHNICAL_LINK
  )
  const ppa = selectedMethods.find(method => method.type === ElectricityProductionMethod.PPA)

  return {
    [ElectricityProductionMethod.GRID]: {
      isSelected: !!grid
    },
    [ElectricityProductionMethod.DIRECT_TECHNICAL_LINK]: {
      isSelected: !!directTechnicalLink,
      isRenewable: directTechnicalLink?.isRenewable,
      info: directTechnicalLink?.info ?? ''
    },
    [ElectricityProductionMethod.PPA]: {
      isSelected: !!ppa,
      isRenewable: ppa?.isRenewable,
      info: ppa?.info ?? ''
    }
  }
}

export default function ProductionRouteElectricity({
  next,
  previous,
  initialValue
}: {
  next: ({
    electricityProductionMethods
  }: {
    electricityProductionMethods: SelectedElectricityProductionMethodsPayload
  }) => void
  previous: () => void
  initialValue: SelectedElectricityProductionMethodsPayload
}) {
  const { t } = useTranslation()

  const [state, setState] = useState<State>(initState(initialValue))

  const isElectricityMethodSelected = useCallback(
    (method: ElectricityProductionMethod) => {
      return state[method].isSelected
    },
    [state]
  )

  const updateSelectedElectricityMethod = useCallback(
    (checked: boolean, method: ElectricityProductionMethod) => {
      return setState({
        ...state,
        [method]: {
          isSelected: checked,
          isRenewable: false
        }
      })
    },
    [state]
  )

  const udpateRenewableState = useCallback(
    (isRenewable: boolean, method: ElectricityProductionMethod) => {
      return setState({
        ...state,
        [method]: {
          ...state[method],
          isRenewable
        }
      })
    },
    [state]
  )

  const updateInfoState = useCallback(
    (info: string, method: ElectricityProductionMethod) => {
      return setState({
        ...state,
        [method]: {
          ...state[method],
          info
        }
      })
    },
    [state]
  )

  const productionMethods = Object.keys(state).map(type => ({
    ...state[type as ElectricityProductionMethod],
    type: type as ElectricityProductionMethod
  }))

  const areAnyMethodsSelected = productionMethods.some(method => method.isSelected)
  const isPPASelected = productionMethods.find(
    method => method.type === ElectricityProductionMethod.PPA && method.isSelected
  )
  const isNextEnabled = areAnyMethodsSelected && isPPASelected ? !!isPPASelected.info : true

  const onNext = useCallback(() => {
    if (areAnyMethodsSelected) {
      next({
        electricityProductionMethods: productionMethods.filter(method => method.isSelected)
      })
    }
  }, [productionMethods, next, areAnyMethodsSelected])

  const onPrevious = () => {
    previous()
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle title={t('productionRoute:electricity.title')} />
        </CardHeader>
        <CardBody className="space-y-4">
          <Alert type="info">{t('productionRoute:electricity.info')}</Alert>
          <p className="text-sm">{t('productionRoute:electricity.description')}</p>
          <SelectListMulti
            items={[
              {
                id: ElectricityProductionMethod.GRID,
                label: t(`common:electricitySources.${ElectricityProductionMethod.GRID}.title`),
                tooltip: t('productionRoute:electricity.methodForm.grid.tooltip'),
                isChecked: isElectricityMethodSelected(ElectricityProductionMethod.GRID)
              },
              {
                id: ElectricityProductionMethod.DIRECT_TECHNICAL_LINK,
                label: t(
                  `common:electricitySources.${ElectricityProductionMethod.DIRECT_TECHNICAL_LINK}.title`
                ),
                tooltip: t('productionRoute:electricity.methodForm.DIRECT_TECHNICAL_LINK.tooltip'),
                isChecked: isElectricityMethodSelected(
                  ElectricityProductionMethod.DIRECT_TECHNICAL_LINK
                ),
                isOpen: isElectricityMethodSelected(
                  ElectricityProductionMethod.DIRECT_TECHNICAL_LINK
                ),
                children: (
                  <MethodForm
                    info={state[ElectricityProductionMethod.DIRECT_TECHNICAL_LINK].info ?? ''}
                    name={ElectricityProductionMethod.DIRECT_TECHNICAL_LINK}
                    isRenewable={
                      state[ElectricityProductionMethod.DIRECT_TECHNICAL_LINK].isRenewable ?? false
                    }
                    updateInfoState={info =>
                      updateInfoState(info, ElectricityProductionMethod.DIRECT_TECHNICAL_LINK)
                    }
                    udpateRenewableState={isRenewable =>
                      udpateRenewableState(
                        isRenewable,
                        ElectricityProductionMethod.DIRECT_TECHNICAL_LINK
                      )
                    }
                    infoLabel={t(
                      `productionRoute:electricity.methodForm.${ElectricityProductionMethod.DIRECT_TECHNICAL_LINK}.info`
                    )}
                  />
                )
              },
              {
                id: ElectricityProductionMethod.PPA,
                label: t(`common:electricitySources.${ElectricityProductionMethod.PPA}.title`),
                isOpen: isElectricityMethodSelected(ElectricityProductionMethod.PPA),
                isChecked: isElectricityMethodSelected(ElectricityProductionMethod.PPA),
                tooltip: t('productionRoute:electricity.methodForm.PPA.tooltip'),
                children: (
                  <MethodForm
                    info={state[ElectricityProductionMethod.PPA].info ?? ''}
                    name={ElectricityProductionMethod.PPA}
                    isRenewable={state[ElectricityProductionMethod.PPA].isRenewable ?? false}
                    updateInfoState={info => updateInfoState(info, ElectricityProductionMethod.PPA)}
                    udpateRenewableState={isRenewable =>
                      udpateRenewableState(isRenewable, ElectricityProductionMethod.PPA)
                    }
                    infoLabel={t(
                      `productionRoute:electricity.methodForm.${ElectricityProductionMethod.PPA}.info`
                    )}
                  />
                )
              }
            ]}
            onChange={(id, checked) => {
              updateSelectedElectricityMethod(checked, id as ElectricityProductionMethod)
            }}
          />
        </CardBody>
      </Card>
      <StepFooter onPrevious={onPrevious} onNext={onNext} isNextEnabled={isNextEnabled} />
    </div>
  )
}
